"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Flame, MapPin, PhoneCall, RefreshCw, ShieldCheck, Wrench, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const services = [{
  title: "Electric Water Heater Repair",
  description: "Rapid diagnosis and fast repair for faulty heating elements, broken thermostats, wiring issues, and tripped reset switches.",
  link: "/water-heater-repair/",
  badge: "Same-Day Service",
  iconName: "wrench"
}, {
  title: "Electric Water Heater Replacement",
  description: "Complete removal and professional replacement with high-efficiency standard tank units sized properly for your household needs.",
  link: "/water-heater-replacement/",
  badge: "Energy Efficient",
  iconName: "refresh"
}, {
  title: "Tankless Electric Installation",
  description: "On-demand hot water solutions tailored for compact spaces and continuous high-performance electrical demand.",
  link: "/tankless-water-heater-service/",
  badge: "Unlimited Hot Water",
  iconName: "zap"
}, {
  title: "Common Electric Heater Problems",
  description: "Troubleshooting no hot water, lukewarm temperatures, electrical buzzing, circuit trips, water leaks, and discolored output.",
  link: "/water-heater-problems/",
  badge: "Diagnostic Guide",
  iconName: "alert"
}, {
  title: "Service Area Coverage",
  description: "Fast local emergency dispatch across Jonesboro, Clayton County, and surrounding South Metro Atlanta communities.",
  link: "/service-areas/",
  badge: "Local Response",
  iconName: "map"
}, {
  title: "Major Brand Support",
  description: "Certified service and genuine OEM parts for Rheem, Bradford White, A. O. Smith, State, EcoSmart, and other top manufacturers.",
  link: "/brands/",
  badge: "OEM Parts",
  iconName: "shield"
}];
function getServiceIcon(name: string) {
  switch (name) {
    case "wrench":
      return <Wrench className="size-6 text-primary" aria-hidden="true" />;
    case "refresh":
      return <RefreshCw className="size-6 text-primary" aria-hidden="true" />;
    case "zap":
      return <Zap className="size-6 text-primary" aria-hidden="true" />;
    case "alert":
      return <AlertTriangle className="size-6 text-primary" aria-hidden="true" />;
    case "map":
      return <MapPin className="size-6 text-primary" aria-hidden="true" />;
    case "shield":
      return <ShieldCheck className="size-6 text-primary" aria-hidden="true" />;
    default:
      return <Flame className="size-6 text-primary" aria-hidden="true" />;
  }
}
export default function ElectricServices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="electricwaterheaterservices" data-nav="light" className="bg-background py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-medium">
            Complete Electric Solutions
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground tracking-tight">
            Electric Water Heater Services
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From emergency repairs and heating element replacements to modern tankless upgrades,
            our licensed Jonesboro technicians keep your hot water flowing reliably.
          </p>
        </div>

        {/* Services Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {services.map((service, index) => <motion.div key={service.title} data-index={index} variants={itemVariants} className="flex h-full">
              <Card className="flex flex-col justify-between w-full border border-border shadow-xs hover:shadow-md transition-shadow duration-300 bg-card rounded-xl">
                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="size-12 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                        {getServiceIcon(service.iconName)}
                      </div>
                      <Badge variant="outline" className="text-xs font-normal">
                        {service.badge}
                      </Badge>
                    </div>
                    <CardTitle className="font-default text-xl font-bold text-card-foreground">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pb-6">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </div>

                <CardFooter className="pt-0">
                  <Button asChild variant="outline" className="w-full justify-between group">
                    <Link to={service.link} className="flex items-center w-full justify-between">
                      <span>Explore Service</span>
                      <ArrowRight className="size-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 transition-transform duration-200" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Emergency Callout Banner */}
        <div className="mt-12 sm:mt-16 bg-secondary rounded-2xl p-6 sm:p-8 lg:p-10 border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-xl text-center md:text-left">
            <h3 className="font-default text-xl sm:text-2xl font-bold text-foreground">
              Need urgent water heater repair in Jonesboro?
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground">
              Our emergency plumbing team is on standby 24/7 to resolve leaks, power faults, and cold water emergencies.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/contact/" className="flex items-center justify-center gap-2">
                <PhoneCall className="size-4" />
                <span>Request 24/7 Service</span>
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/water-heater-problems/">
                <span>View Problem Guide</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}